import React, { useCallback, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import api from '@utils/axios';
import { translate } from '@utils/i18n';
import { updateStatusProposalPath } from 'routes';

import StatusChangeContext from './Index/nested/StatusChangeContext';
import StatusModal from './Index/nested/StatusModal';

const namespace = 'features.proposals.status_modal';

// IP-15353 / Aha INS-I-108, the ask as originally written: change a proposal's
// status from the detail screen, without going into Edit first.
//
// Mounted into the legacy detail screen's main action toolbar, alongside Create,
// Note and Collect. That screen is ERB, so this renders its own trigger as well as
// the modal, and supplies the context the modal reads — the listing provides the
// same context from its grid.
//
// Only the legacy screen needs this. `has_proposals_detail_v2` swaps the whole
// detail screen for a React one, and it is on for a single tenant; every other
// tenant sees the ERB screen this mounts into.
function ProposalStatusAction({ proposalId, proposalNumber, statusId, statuses }) {
  const [open, setOpen] = useState(false);

  // The detail screen renders status history, the approval block and the action
  // list itself from server state, so a targeted repaint would leave parts of the
  // page stale. Reloading is the honest option here — unlike the listing, where
  // only one row's cells change.
  const onChangeStatus = useCallback(async (id, proposalStatusId) => {
    await api.patch(updateStatusProposalPath({ id, format: 'json', _options: true }), {
      proposal_status_id: proposalStatusId
    });

    window.location.reload();
  }, []);

  const contextValue = useMemo(() => ({ statuses, onChangeStatus }), [statuses, onChangeStatus]);

  const dataItem = useMemo(
    () => ({ id: proposalId, number: proposalNumber, statusId }),
    [proposalId, proposalNumber, statusId]
  );

  return (
    <StatusChangeContext.Provider value={contextValue}>
      {/* qmb-control, not qmb-popup__link: this sits in the toolbar row with Note,
          which is a qmb-control link, so it has to read as one of that set. */}
      <button type="button" className="qmb-control" onClick={() => setOpen(true)}>
        <i className="fa-light fa-arrows-rotate" /> {translate('button', { namespace })}
      </button>

      {open && <StatusModal dataItem={dataItem} onClose={() => setOpen(false)} />}
    </StatusChangeContext.Provider>
  );
}

ProposalStatusAction.propTypes = {
  proposalId: PropTypes.number.isRequired,
  proposalNumber: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  statusId: PropTypes.number,
  statuses: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.number,
      title: PropTypes.string,
      nameAbbr: PropTypes.string
    })
  )
};

ProposalStatusAction.defaultProps = {
  proposalNumber: null,
  statusId: null,
  statuses: []
};

export default ProposalStatusAction;
